import { useState, useEffect, useCallback, useRef } from "react";
import "./admin.css";
import { assetUrl } from "../../api";
import {
  adminStyles,
  adminScenes,
  adminProducts,
  adminCategories,
  adminCollections,
  uploadAsset,
  analyzeStyle,
  analyzeMockupBg,
  previewComposite,
  type AdminStyle,
  type AdminCategory,
  type AdminCollection,
  type SceneConfig,
  type ScenesMap,
  type AdminProduct,
  adminOrders,
  type AdminOrder,
} from "./adminApi";

// ═══════════════════════════════════════════════════
// Reusable confirm dialog — replaces native window.confirm() for destructive
// actions so users get a styled, focus-trapped modal instead of a browser
// chrome prompt that's easy to miss/dismiss. Backdrop click cancels.
// ═══════════════════════════════════════════════════
function ConfirmDialog({
  title,
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  danger = true,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div
        className="admin-modal"
        style={{ maxWidth: 440 }}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
      >
        <h3>{title}</h3>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "#444", marginBottom: 20 }}>{message}</div>
        <div className="admin-form__actions">
          <button className="admin-btn" onClick={onCancel} autoFocus>{cancelLabel}</button>
          <button
            className={danger ? "admin-btn admin-btn--danger" : "admin-btn admin-btn--primary"}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Login Gate
// ═══════════════════════════════════════════════════
function LoginGate({ onLogin }: { onLogin: () => void }) {
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  async function tryLogin() {
    sessionStorage.setItem("admin_token", token);
    try {
      await adminStyles.list();
      onLogin();
    } catch {
      setErr("Invalid token");
      sessionStorage.removeItem("admin_token");
    }
  }
  return (
    <div className="admin-login">
      <div className="admin-login__card">
        <h2>🔐 Admin Panel</h2>
        <p>Enter your admin secret to continue.</p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && tryLogin()}
          placeholder="Admin Secret"
          autoFocus
        />
        {err && <p className="admin-err">{err}</p>}
        <button onClick={tryLogin}>Login</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Shared drag-reorder hook
// Native HTML5 drag, no deps. Optimistic local reorder + persist via saveOrder.
// Visual feedback: `is-dragging` on the picked-up card, `is-drop-target` on
// the card being hovered. Caller composes these class names into its own
// className string so existing styles aren't disturbed.
// ═══════════════════════════════════════════════════
function useDragReorder<T extends { id: string }>(
  items: T[],
  setItems: (next: T[]) => void,
  saveOrder: (orderedIds: string[]) => Promise<unknown>,
  onError: (msg: string) => void,
) {
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  function getDragHandlers(i: number) {
    return {
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        setDraggedIdx(i);
        e.dataTransfer.effectAllowed = "move";
        // Some browsers (Firefox) require dataTransfer data for drag to fire.
        e.dataTransfer.setData("text/plain", String(i));
      },
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverIdx !== i) setDragOverIdx(i);
      },
      onDrop: async (e: React.DragEvent) => {
        e.preventDefault();
        const from = draggedIdx;
        setDragOverIdx(null);
        setDraggedIdx(null);
        if (from === null || from === i) return;
        const next = [...items];
        const [moved] = next.splice(from, 1);
        if (!moved) return;
        next.splice(i, 0, moved);
        setItems(next); // optimistic — UI updates before the network round-trip
        try {
          await saveOrder(next.map((it) => it.id));
        } catch (err) {
          onError(err instanceof Error ? err.message : String(err));
        }
      },
      onDragEnd: () => {
        setDraggedIdx(null);
        setDragOverIdx(null);
      },
    };
  }

  return { draggedIdx, dragOverIdx, getDragHandlers };
}

// ═══════════════════════════════════════════════════
// Styles Manager
// ═══════════════════════════════════════════════════

// Slug the label → snake_case ASCII id. Shared by Auto-Create and the manual
// "New Template" → ID prefill flow.
function slugifyId(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Return a fresh id not already in `used`. If `base` is taken, append _2, _3…
// until free. Falls back to a timestamp id if `base` is empty.
function uniqueId(base: string, used: Set<string>): string {
  const safe = base || `style_${Date.now()}`;
  if (!used.has(safe)) return safe;
  let i = 2;
  while (used.has(`${safe}_${i}`)) i++;
  return `${safe}_${i}`;
}

// Sequential collection-scoped id: <collectionId>_<n> with the smallest free
// n≥1. Batch auto-create uses this so every template in a collection gets a
// readable, non-colliding id (girls_1, girls_2, …). `used` must include ids
// created earlier in the same batch so a run never collides with itself.
function nextCollectionId(collectionId: string, used: Set<string>): string {
  let n = 1;
  while (used.has(`${collectionId}_${n}`)) n++;
  return `${collectionId}_${n}`;
}

function StylesManager() {
  const [styles, setStyles] = useState<AdminStyle[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  // `isNew=true` means ID input is editable + we route Save through POST
  // (with client-side duplicate guard). `isNew=false` locks the ID and PUTs.
  const [editing, setEditing] = useState<{ style: AdminStyle; isNew: boolean } | null>(null);
  const [deleting, setDeleting] = useState<AdminStyle | null>(null);
  // Visual filters for the gallery — lets the admin focus on one slice when
  // the list gets long. "__all__" disables that axis. "__none__" (collection
  // only) shows unassigned templates. Filters don't change persisted order.
  const [filterCat, setFilterCat] = useState<string>("__all__");
  const [filterCol, setFilterCol] = useState<string>("__all__");
  const [msg, setMsg] = useState("");
  const autoFileRef = useRef<HTMLInputElement>(null);
  // Batch auto-create: pick one collection + N images → analyze each with
  // Gemini and save them all as <collection>_<n> without a per-item review.
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchCollection, setBatchCollection] = useState("");
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [batchResult, setBatchResult] = useState<{ created: number; failures: { name: string; reason: string }[] } | null>(null);

  const load = useCallback(async () => {
    const [s, cats, cols] = await Promise.all([
      adminStyles.list(),
      adminCategories.list(),
      adminCollections.list(),
    ]);
    setStyles(s.styles);
    setCategories(cats.categories);
    setCollections(cols.collections);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Errors stay until the next action — auto-dismiss only success so users
  // never miss why something failed. (Old 2.5s timeout was hiding real bugs.)
  function flash(m: string) {
    setMsg(m);
    if (!m.startsWith("❌")) setTimeout(() => setMsg(""), 2500);
  }

  // Drag reorder runs on the filtered subset so dragging behaves as the user
  // sees it. "__all__" on both axes = whole list (the fast path).
  function matchesFilters(s: AdminStyle): boolean {
    if (filterCat !== "__all__" && s.category !== filterCat) return false;
    if (filterCol !== "__all__") {
      const sCol = s.collection ?? "";
      const target = filterCol === "__none__" ? "" : filterCol;
      if (sCol !== target) return false;
    }
    return true;
  }
  const isFiltered = filterCat !== "__all__" || filterCol !== "__all__";
  const visible = isFiltered ? styles.filter(matchesFilters) : styles;

  const { draggedIdx, dragOverIdx, getDragHandlers } = useDragReorder(
    visible,
    (next) => {
      // Splice the reordered visible slice back into the full styles array,
      // preserving non-visible items in place.
      if (!isFiltered) {
        setStyles(next);
      } else {
        const others = styles.filter((s) => !matchesFilters(s));
        setStyles([...next, ...others]);
      }
    },
    async (order) => {
      // Build the full order: filtered subset first (in their new order),
      // then everything else in current order. For the simple no-filter case
      // this is just `order`.
      const fullOrder = !isFiltered
        ? order
        : [...order, ...styles.filter((s) => !matchesFilters(s)).map((s) => s.id)];
      await adminStyles.reorder(fullOrder);
      flash("✅ Order saved");
    },
    (m) => {
      flash(`❌ Reorder failed: ${m}`);
      load(); // revert to server state on failure
    },
  );

  async function save(s: AdminStyle, isNew: boolean) {
    try {
      if (isNew) {
        // Client-side guard — server also returns 409 but we want a clear
        // inline message before round-tripping. The race against another
        // admin tab creating the same id is still covered by the 409 catch.
        if (styles.find((x) => x.id === s.id)) {
          flash(`❌ ID "${s.id}" already exists. Use a different ID (try adding a prefix/suffix).`);
          return;
        }
        await adminStyles.create(s);
        flash("✅ Template created");
      } else {
        await adminStyles.update(s.id, s);
        flash("✅ Template updated");
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      console.error("[admin] style save failed:", e);
      flash(`❌ ${e instanceof Error ? e.message : "Error"}`);
    }
  }

  async function toggle(s: AdminStyle) {
    await adminStyles.update(s.id, { enabled: !s.enabled });
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await adminStyles.delete(deleting.id);
      flash("🗑️ Template deleted permanently");
      setDeleting(null);
      load();
    } catch (e: unknown) {
      flash(`❌ Delete failed: ${e instanceof Error ? e.message : "Error"}`);
    }
  }

  async function handleAutoCreate(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Always clear the file input first — otherwise picking the same file
    // twice won't re-fire `change` and the flow looks dead from the user's POV.
    if (autoFileRef.current) autoFileRef.current.value = "";
    if (!file) return;

    setMsg("✨ Uploading image…");
    try {
      // 1. Upload the image to R2 (or filesystem in dev) → returns a stable URL.
      const { url } = await uploadAsset(file, "styles");
      setMsg("🧠 Asking Gemini to read this image…");
      // 2. Run Gemini Vision over the same file to extract template fields.
      const { analysis } = await analyzeStyle(file);
      console.log("[admin] auto-create analysis:", analysis);

      // 3. Open editor with pre-filled data — guard every field in case Gemini
      // returned partial JSON. A safe default lets the user fix it manually
      // instead of crashing on `.toLowerCase()` of undefined.
      const label = (analysis?.label ?? "").toString().trim() || "Untitled Template";
      // Collision-safe slug — if Gemini returns the same label twice we'd
      // otherwise generate the same id and silently overwrite the existing
      // template via the (now blocked) PUT path. Append _2/_3/… to dedupe.
      const usedIds = new Set(styles.map((x) => x.id));
      const safeId = uniqueId(slugifyId(label), usedIds);
      const safeGradient: [string, string] =
        Array.isArray(analysis?.gradient) && analysis.gradient.length === 2
          ? [analysis.gradient[0], analysis.gradient[1]]
          : ["#333333", "#666666"];
      // Map Gemini's category to an existing AdminCategory id; otherwise
      // fall back to the first defined category (or empty string).
      const geminiCat = (analysis?.category ?? "").toString();
      const fallbackCat = categories[0]?.id ?? "modern";
      const safeCategory = categories.find((c) => c.id === geminiCat)?.id ?? fallbackCat;

      setEditing({
        style: {
          id: safeId,
          imageUrl: url,
          label,
          category: safeCategory,
          // Collection isn't part of Gemini's vision schema (audience targeting
          // isn't visually discernible) — leave unassigned, admin picks below.
          collection: "",
          tagline: analysis?.tagline || "",
          gradient: safeGradient,
          promptTemplate: analysis?.promptTemplate || "",
          enabled: true,
          sortOrder: styles.length,
        },
        isNew: true,
      });
      setMsg(""); // editor is now visible — clear the status banner
    } catch (err: unknown) {
      console.error("[admin] auto-create failed:", err);
      flash(`❌ AI Analysis failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  }

  // Batch: analyze + create N images into the chosen collection SEQUENTIALLY.
  // Sequential (not parallel) avoids racing the server's read-modify-write of
  // styles.json, and lets each id derive from the growing `used` set. Each
  // image is one Gemini Vision call — admin path only, so within cost rules.
  async function runBatch() {
    if (!batchCollection || batchFiles.length === 0 || batchRunning) return;
    setBatchRunning(true);
    setBatchResult(null);
    const used = new Set(styles.map((x) => x.id));
    const failures: { name: string; reason: string }[] = [];
    let created = 0;
    for (let i = 0; i < batchFiles.length; i++) {
      const file = batchFiles[i]!;
      setBatchProgress({ done: i, total: batchFiles.length });
      try {
        const { url } = await uploadAsset(file, "styles");
        const { analysis } = await analyzeStyle(file);
        const id = nextCollectionId(batchCollection, used);
        const label =
          (analysis?.label ?? "").toString().trim() ||
          file.name.replace(/\.[^.]+$/, "") ||
          id;
        const gradient: [string, string] =
          Array.isArray(analysis?.gradient) && analysis.gradient.length === 2
            ? [analysis.gradient[0], analysis.gradient[1]]
            : ["#333333", "#666666"];
        const geminiCat = (analysis?.category ?? "").toString();
        const category =
          categories.find((c) => c.id === geminiCat)?.id ?? categories[0]?.id ?? "modern";
        await adminStyles.create({
          id,
          imageUrl: url,
          label,
          category,
          collection: batchCollection,
          tagline: analysis?.tagline || "",
          gradient,
          promptTemplate: analysis?.promptTemplate || "",
          enabled: true,
          sortOrder: styles.length + created,
        });
        used.add(id); // only after success, so a failed item leaves no id gap
        created++;
      } catch (e) {
        failures.push({ name: file.name, reason: e instanceof Error ? e.message : String(e) });
      }
    }
    setBatchProgress({ done: batchFiles.length, total: batchFiles.length });
    setBatchRunning(false);
    setBatchResult({ created, failures });
    setBatchFiles([]);
    await load();
  }

  function openNew() {
    setEditing({
      style: {
        id: "",
        label: "",
        category: categories[0]?.id ?? "modern",
        // Default to the active collection filter when one is selected — admin
        // is presumably curating that collection right now.
        collection: filterCol !== "__all__" && filterCol !== "__none__" ? filterCol : "",
        tagline: "",
        gradient: ["#333", "#666"],
        promptTemplate: "",
        enabled: true,
        sortOrder: styles.length,
      },
      isNew: true,
    });
  }

  // Category / collection label lookups for badges. Falls back to id if a
  // style points at a deleted record (shouldn't happen with the in-use
  // guards, but harmless to show the raw id).
  const catLabel = (id: string) => categories.find((c) => c.id === id)?.label ?? id;
  const colLabel = (id?: string) => (id ? collections.find((c) => c.id === id)?.label ?? id : "");

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>📋 Design Templates</h3>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#555" }}>
            Category:
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #d0d4dc", borderRadius: 6, fontSize: 13 }}
            >
              <option value="__all__">All ({styles.length})</option>
              {categories.map((c) => {
                const count = styles.filter((s) => s.category === c.id).length;
                return <option key={c.id} value={c.id}>{c.label} ({count})</option>;
              })}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#555" }}>
            Collection:
            <select
              value={filterCol}
              onChange={(e) => setFilterCol(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #d0d4dc", borderRadius: 6, fontSize: 13 }}
            >
              <option value="__all__">All ({styles.length})</option>
              <option value="__none__">— Unassigned ({styles.filter((s) => !s.collection).length})</option>
              {collections.map((c) => {
                const count = styles.filter((s) => s.collection === c.id).length;
                return <option key={c.id} value={c.id}>{c.label} ({count})</option>;
              })}
            </select>
          </label>
          <button className="admin-btn admin-btn--primary" onClick={openNew}>+ New Template</button>
          <input type="file" ref={autoFileRef} hidden accept="image/*" onChange={handleAutoCreate} />
          <button className="admin-btn" style={{ background: "linear-gradient(135deg, #10b981, #059669)", color: "white", border: "none" }} onClick={() => autoFileRef.current?.click()}>
            ✨ Auto-Create from Image
          </button>
          <button
            className="admin-btn"
            style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "white", border: "none" }}
            onClick={() => {
              setBatchResult(null);
              setBatchFiles([]);
              setBatchProgress(null);
              // Preselect the active collection filter when one is chosen.
              setBatchCollection(filterCol !== "__all__" && filterCol !== "__none__" ? filterCol : "");
              setBatchOpen(true);
            }}
          >
            ✨ Batch Auto-Create
          </button>
        </div>
      </div>
      {msg && (
        <div
          className={"admin-flash" + (msg.startsWith("❌") ? " admin-flash--error" : "")}
          onClick={() => setMsg("")}
          role="button"
          title="Click to dismiss"
        >
          {msg}
        </div>
      )}

      <div className="admin-canvas-gallery">
        {visible.map((s, i) => (
          <div
            key={s.id}
            {...getDragHandlers(i)}
            className={
              "admin-canvas-card" +
              (s.enabled === false ? " admin-canvas-card--disabled" : "") +
              (draggedIdx === i ? " is-dragging" : "") +
              (dragOverIdx === i && draggedIdx !== i ? " is-drop-target" : "")
            }
          >
            <div className="admin-drag-handle" aria-hidden title="Drag to reorder">⋮⋮</div>
            <div className="admin-canvas-card__frame">
              <div className="admin-canvas-card__art" style={{ background: `linear-gradient(135deg, ${s.gradient[0]}, ${s.gradient[1]})` }}>
                {s.imageUrl && <img src={assetUrl(s.imageUrl)} alt={s.label} />}
              </div>
            </div>
            <div className="admin-canvas-card__info">
              <h4 className="admin-canvas-card__title">{s.label}</h4>
              <div className="admin-canvas-card__meta" style={{ flexWrap: "wrap" }}>
                <span className="admin-badge">{catLabel(s.category)}</span>
                {s.collection && (
                  <span
                    className="admin-badge"
                    style={{ background: "linear-gradient(135deg, #c9a14a, #b08a3a)" }}
                    title="Collection"
                  >
                    {colLabel(s.collection)}
                  </span>
                )}
                <code className="admin-card__id">{s.id}</code>
              </div>
              <p className="admin-canvas-card__tagline">{s.tagline}</p>
              <div className="admin-canvas-card__actions">
                <button className="admin-btn" onClick={() => setEditing({ style: { ...s }, isNew: false })}>✏️ Edit</button>
                <button className="admin-btn" onClick={() => toggle(s)}>
                  {s.enabled !== false ? "🔴 Disable" : "🟢 Enable"}
                </button>
                <button className="admin-btn admin-btn--danger" onClick={() => setDeleting(s)}>🗑️</button>
              </div>
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: "#888", fontSize: 14 }}>
            {!isFiltered ? "No templates yet." : (() => {
              const bits: string[] = [];
              if (filterCat !== "__all__") bits.push(`Category: ${catLabel(filterCat)}`);
              if (filterCol === "__none__") bits.push("Collection: Unassigned");
              else if (filterCol !== "__all__") bits.push(`Collection: ${colLabel(filterCol)}`);
              return `No templates match (${bits.join(", ")}).`;
            })()}
          </div>
        )}
      </div>

      {editing && (
        <StyleEditor
          style={editing.style}
          isNew={editing.isNew}
          categories={categories}
          collections={collections}
          existingIds={styles.map((x) => x.id)}
          onSave={(s) => save(s, editing.isNew)}
          onCancel={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this template?"
          message={
            <>
              You're about to permanently delete <strong>{deleting.label}</strong> (<code>{deleting.id}</code>).
              <br /><br />
              This cannot be undone. Existing customer orders that reference this template will keep their data, but the template will no longer appear in the catalog or admin.
            </>
          }
          confirmLabel="Delete permanently"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      {batchOpen && (
        <div className="admin-modal-backdrop" onClick={() => { if (!batchRunning) setBatchOpen(false); }}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3 style={{ marginTop: 0 }}>✨ Batch Auto-Create from Images</h3>
            <p style={{ fontSize: 13, color: "#555", marginTop: 0 }}>
              Pick a collection and upload multiple template images. Each image is
              analyzed by Gemini and saved as <code>&lt;collection&gt;_N</code>
              {" "}automatically — no per-item review. Edit any of them afterwards
              from the list.
            </p>

            <label style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 6 }}>Collection</label>
            <select
              value={batchCollection}
              onChange={(e) => setBatchCollection(e.target.value)}
              disabled={batchRunning}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #d0d4dc", borderRadius: 6, fontSize: 14, marginBottom: 14 }}
            >
              <option value="">— Select a collection —</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.label} ({c.id})</option>)}
            </select>

            <input
              type="file"
              accept="image/*"
              multiple
              disabled={batchRunning}
              onChange={(e) => setBatchFiles(Array.from(e.target.files ?? []))}
              style={{ display: "block", marginBottom: 12, fontSize: 13 }}
            />

            {batchFiles.length > 0 && !batchResult && (
              <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
                {batchFiles.length} image(s) selected.{" "}
                {batchCollection
                  ? <>First new id: <code>{nextCollectionId(batchCollection, new Set(styles.map((x) => x.id)))}</code></>
                  : "Pick a collection to generate ids."}
              </div>
            )}

            {batchRunning && batchProgress && (
              <div style={{ fontSize: 13, color: "#333", marginBottom: 12 }}>
                🧠 Processing {batchProgress.done}/{batchProgress.total}…
              </div>
            )}

            {batchResult && (
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                ✅ Created {batchResult.created} template(s).
                {batchResult.failures.length > 0 && (
                  <div style={{ marginTop: 8, color: "#b91c1c" }}>
                    ⚠ {batchResult.failures.length} failed:
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                      {batchResult.failures.map((f, i) => (
                        <li key={i}><strong>{f.name}</strong>: {f.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button className="admin-btn" disabled={batchRunning} onClick={() => setBatchOpen(false)}>
                {batchResult ? "Close" : "Cancel"}
              </button>
              {!batchResult && (
                <button
                  className="admin-btn admin-btn--primary"
                  disabled={batchRunning || !batchCollection || batchFiles.length === 0}
                  onClick={runBatch}
                >
                  {batchRunning ? "Processing…" : `Create ${batchFiles.length || ""} template(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StyleEditor({ style, isNew, categories, collections, existingIds, onSave, onCancel }: {
  style: AdminStyle;
  isNew: boolean;
  categories: AdminCategory[];
  collections: AdminCollection[];
  existingIds: string[];
  onSave: (s: AdminStyle) => void;
  onCancel: () => void;
}) {
  const [s, setS] = useState<AdminStyle>(style);
  const [uploading, setUploading] = useState(false);

  function set<K extends keyof AdminStyle>(k: K, v: AdminStyle[K]) {
    setS((prev) => ({ ...prev, [k]: v }));
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadAsset(file, "styles");
      set("imageUrl", url);
    } catch { /* ignore */ }
    setUploading(false);
  }

  // Live-validation of the ID field — only relevant on the new-template flow
  // since edit locks the ID. Show "in use" if the typed id collides AND it's
  // not the record's own id (defensive, even though edit locks it).
  const idCollision = isNew && s.id.length > 0 && existingIds.includes(s.id) && s.id !== style.id;
  const idEmpty = isNew && s.id.trim().length === 0;
  const canSave = !idCollision && !idEmpty && s.label.trim().length > 0;

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isNew ? "New Template" : `Edit: ${style.label}`}</h3>

        <div className="admin-form">
          <label>ID (unique, lowercase) {!isNew && <small>· locked on existing templates</small>}
            <input
              value={s.id}
              onChange={(e) => set("id", e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase())}
              disabled={!isNew}
              style={idCollision ? { borderColor: "#e74c3c" } : undefined}
            />
            {idCollision && (
              <small style={{ color: "#e74c3c", fontWeight: 500 }}>
                ⚠ This ID already exists. Try a prefix/suffix (e.g. <code>{s.id}_v2</code>).
              </small>
            )}
            {idEmpty && (
              <small style={{ color: "#888" }}>Enter an ID, or fill the Label to auto-generate one.</small>
            )}
          </label>

          <label>Label
            <input
              value={s.label}
              onChange={(e) => {
                const newLabel = e.target.value;
                setS((prev) => {
                  const next = { ...prev, label: newLabel };
                  // Auto-sync id from label on new templates if id is empty
                  // OR id was untouched since last auto-sync (matches the
                  // previous label's slug). Stops overwriting once user types
                  // a custom id (collision/prefix workflow).
                  if (isNew) {
                    const prevSlug = slugifyId(prev.label);
                    if (!prev.id || prev.id === prevSlug) {
                      next.id = slugifyId(newLabel);
                    }
                  }
                  return next;
                });
              }}
            />
          </label>

          <label>Category
            <select value={s.category} onChange={(e) => set("category", e.target.value)}>
              {categories.length === 0 && <option value="">— No categories yet — add one in the Categories tab</option>}
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </label>

          <label>Collection <small>(audience — Girls, Boys, …)</small>
            <select value={s.collection ?? ""} onChange={(e) => set("collection", e.target.value)}>
              <option value="">— Unassigned —</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {collections.length === 0 && (
              <small style={{ color: "#888" }}>
                No collections yet. Add one in the Collections tab to group templates by audience.
              </small>
            )}
          </label>

          <label>Tagline
            <input value={s.tagline} onChange={(e) => set("tagline", e.target.value)} />
          </label>

          <label>Prompt Template <small>(use {"{dadName}"} placeholder)</small>
            <textarea rows={4} value={s.promptTemplate} onChange={(e) => set("promptTemplate", e.target.value)} />
          </label>

          <div className="admin-form__row">
            <label>Gradient Start
              <input type="color" value={s.gradient[0]} onChange={(e) => set("gradient", [e.target.value, s.gradient[1]])} />
            </label>
            <label>Gradient End
              <input type="color" value={s.gradient[1]} onChange={(e) => set("gradient", [s.gradient[0], e.target.value])} />
            </label>
            <div className="admin-form__preview" style={{ background: `linear-gradient(135deg, ${s.gradient[0]}, ${s.gradient[1]})` }} />
          </div>

          <label>Preview Image
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            {uploading && <span>Uploading...</span>}
            {s.imageUrl && <img src={assetUrl(s.imageUrl)} alt="preview" className="admin-form__thumb" />}
          </label>

          <div className="admin-form__actions">
            <button className="admin-btn" onClick={onCancel}>Cancel</button>
            <button
              className="admin-btn admin-btn--primary"
              onClick={() => onSave(s)}
              disabled={!canSave}
              style={!canSave ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Categories Manager
// Lets the admin create / rename / reorder / delete the categories used by
// the Templates dropdown. Delete is refused server-side if any template
// still references the category — keeps the data consistent.
// ═══════════════════════════════════════════════════
function CategoriesManager() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [styles, setStyles] = useState<AdminStyle[]>([]);
  const [editing, setEditing] = useState<{ cat: AdminCategory; isNew: boolean } | null>(null);
  const [deleting, setDeleting] = useState<AdminCategory | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([adminCategories.list(), adminStyles.list()]);
    setCategories(c.categories);
    setStyles(s.styles);
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(m: string) {
    setMsg(m);
    if (!m.startsWith("❌")) setTimeout(() => setMsg(""), 2500);
  }

  const { draggedIdx, dragOverIdx, getDragHandlers } = useDragReorder(
    categories,
    setCategories,
    async (order) => {
      await adminCategories.reorder(order);
      flash("✅ Order saved");
    },
    (m) => {
      flash(`❌ Reorder failed: ${m}`);
      load();
    },
  );

  async function save(c: AdminCategory, isNew: boolean) {
    try {
      if (isNew) {
        if (categories.find((x) => x.id === c.id)) {
          flash(`❌ ID "${c.id}" already exists.`);
          return;
        }
        await adminCategories.create(c);
        flash("✅ Category created");
      } else {
        await adminCategories.update(c.id, { label: c.label });
        flash("✅ Category renamed");
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      flash(`❌ ${e instanceof Error ? e.message : "Error"}`);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await adminCategories.delete(deleting.id);
      flash("🗑️ Category deleted");
      setDeleting(null);
      load();
    } catch (e: unknown) {
      flash(`❌ ${e instanceof Error ? e.message : "Delete failed"}`);
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>🏷 Categories</h3>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => setEditing({ cat: { id: "", label: "", sortOrder: categories.length }, isNew: true })}
        >
          + New Category
        </button>
      </div>
      {msg && (
        <div
          className={"admin-flash" + (msg.startsWith("❌") ? " admin-flash--error" : "")}
          onClick={() => setMsg("")}
          role="button"
          title="Click to dismiss"
        >
          {msg}
        </div>
      )}

      <div className="admin-grid">
        {categories.map((c, i) => {
          const inUse = styles.filter((s) => s.category === c.id).length;
          return (
            <div
              key={c.id}
              {...getDragHandlers(i)}
              className={
                "admin-card" +
                (draggedIdx === i ? " is-dragging" : "") +
                (dragOverIdx === i && draggedIdx !== i ? " is-drop-target" : "")
              }
            >
              <div className="admin-drag-handle" aria-hidden title="Drag to reorder">⋮⋮</div>
              <div className="admin-card__body">
                <h4>{c.label}</h4>
                <code className="admin-card__id">{c.id}</code>
                <p className="admin-card__tagline">
                  {inUse} template{inUse === 1 ? "" : "s"} in this category
                </p>
              </div>
              <div className="admin-card__actions">
                <button className="admin-btn" onClick={() => setEditing({ cat: { ...c }, isNew: false })}>
                  ✏️ Rename
                </button>
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={() => setDeleting(c)}
                  disabled={inUse > 0}
                  title={inUse > 0 ? "Reassign templates first" : "Delete this category"}
                  style={inUse > 0 ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })}
        {categories.length === 0 && (
          <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: "#888", fontSize: 14 }}>
            No categories yet. Click "+ New Category" to start.
          </div>
        )}
      </div>

      {editing && (
        <CategoryEditor
          category={editing.cat}
          isNew={editing.isNew}
          existingIds={categories.map((c) => c.id)}
          onSave={(c) => save(c, editing.isNew)}
          onCancel={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this category?"
          message={
            <>
              Permanently delete <strong>{deleting.label}</strong> (<code>{deleting.id}</code>)?
              <br /><br />
              No templates currently use this category, so deletion is safe.
            </>
          }
          confirmLabel="Delete category"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Collections Manager — same shape as CategoriesManager, different axis.
// Kept as a separate component (instead of a generic) so the labels, hint
// text, and in-use guard messages can speak the right vocabulary without
// generics + i18n indirection.
// ═══════════════════════════════════════════════════
function CollectionsManager() {
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  const [styles, setStyles] = useState<AdminStyle[]>([]);
  const [editing, setEditing] = useState<{ col: AdminCollection; isNew: boolean } | null>(null);
  const [deleting, setDeleting] = useState<AdminCollection | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([adminCollections.list(), adminStyles.list()]);
    setCollections(c.collections);
    setStyles(s.styles);
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(m: string) {
    setMsg(m);
    if (!m.startsWith("❌")) setTimeout(() => setMsg(""), 2500);
  }

  const { draggedIdx, dragOverIdx, getDragHandlers } = useDragReorder(
    collections,
    setCollections,
    async (order) => {
      await adminCollections.reorder(order);
      flash("✅ Order saved");
    },
    (m) => {
      flash(`❌ Reorder failed: ${m}`);
      load();
    },
  );

  async function save(c: AdminCollection, isNew: boolean) {
    try {
      if (isNew) {
        if (collections.find((x) => x.id === c.id)) {
          flash(`❌ ID "${c.id}" already exists.`);
          return;
        }
        await adminCollections.create(c);
        flash("✅ Collection created");
      } else {
        await adminCollections.update(c.id, { label: c.label });
        flash("✅ Collection renamed");
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      flash(`❌ ${e instanceof Error ? e.message : "Error"}`);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await adminCollections.delete(deleting.id);
      flash("🗑️ Collection deleted");
      setDeleting(null);
      load();
    } catch (e: unknown) {
      flash(`❌ ${e instanceof Error ? e.message : "Delete failed"}`);
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>📚 Collections <small style={{ fontSize: 13, fontWeight: 400, color: "#888", marginLeft: 8 }}>· audience groups (Girls, Boys, …)</small></h3>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => setEditing({ col: { id: "", label: "", sortOrder: collections.length }, isNew: true })}
        >
          + New Collection
        </button>
      </div>
      {msg && (
        <div
          className={"admin-flash" + (msg.startsWith("❌") ? " admin-flash--error" : "")}
          onClick={() => setMsg("")}
          role="button"
          title="Click to dismiss"
        >
          {msg}
        </div>
      )}

      <div className="admin-grid">
        {collections.map((c, i) => {
          const inUse = styles.filter((s) => s.collection === c.id).length;
          return (
            <div
              key={c.id}
              {...getDragHandlers(i)}
              className={
                "admin-card" +
                (draggedIdx === i ? " is-dragging" : "") +
                (dragOverIdx === i && draggedIdx !== i ? " is-drop-target" : "")
              }
            >
              <div className="admin-drag-handle" aria-hidden title="Drag to reorder">⋮⋮</div>
              <div className="admin-card__body">
                <h4>{c.label}</h4>
                <code className="admin-card__id">{c.id}</code>
                <p className="admin-card__tagline">
                  {inUse} template{inUse === 1 ? "" : "s"} in this collection
                </p>
              </div>
              <div className="admin-card__actions">
                <button className="admin-btn" onClick={() => setEditing({ col: { ...c }, isNew: false })}>
                  ✏️ Rename
                </button>
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={() => setDeleting(c)}
                  disabled={inUse > 0}
                  title={inUse > 0 ? "Reassign templates first" : "Delete this collection"}
                  style={inUse > 0 ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })}
        {collections.length === 0 && (
          <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: "#888", fontSize: 14 }}>
            No collections yet. Click "+ New Collection" to add Girls / Boys / etc.
          </div>
        )}
      </div>

      {editing && (
        <CollectionEditor
          collection={editing.col}
          isNew={editing.isNew}
          existingIds={collections.map((c) => c.id)}
          onSave={(c) => save(c, editing.isNew)}
          onCancel={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this collection?"
          message={
            <>
              Permanently delete <strong>{deleting.label}</strong> (<code>{deleting.id}</code>)?
              <br /><br />
              No templates currently use this collection, so deletion is safe.
            </>
          }
          confirmLabel="Delete collection"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function CollectionEditor({ collection, isNew, existingIds, onSave, onCancel }: {
  collection: AdminCollection;
  isNew: boolean;
  existingIds: string[];
  onSave: (c: AdminCollection) => void;
  onCancel: () => void;
}) {
  const [c, setC] = useState<AdminCollection>(collection);
  const idCollision = isNew && c.id.length > 0 && existingIds.includes(c.id);
  const idEmpty = isNew && c.id.trim().length === 0;
  const labelEmpty = c.label.trim().length === 0;
  const canSave = !idCollision && !idEmpty && !labelEmpty;

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3>{isNew ? "New Collection" : `Rename: ${collection.label}`}</h3>
        <div className="admin-form">
          <label>ID (unique, lowercase) {!isNew && <small>· locked on existing collections</small>}
            <input
              value={c.id}
              onChange={(e) => setC((prev) => ({ ...prev, id: e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase() }))}
              disabled={!isNew}
              style={idCollision ? { borderColor: "#e74c3c" } : undefined}
            />
            {idCollision && <small style={{ color: "#e74c3c", fontWeight: 500 }}>⚠ Already exists.</small>}
          </label>
          <label>Display Label
            <input
              value={c.label}
              onChange={(e) => {
                const newLabel = e.target.value;
                setC((prev) => {
                  const next = { ...prev, label: newLabel };
                  if (isNew) {
                    const prevSlug = slugifyId(prev.label);
                    if (!prev.id || prev.id === prevSlug) {
                      next.id = slugifyId(newLabel);
                    }
                  }
                  return next;
                });
              }}
              placeholder="Girls, Boys, Weddings…"
            />
          </label>
          <div className="admin-form__actions">
            <button className="admin-btn" onClick={onCancel}>Cancel</button>
            <button
              className="admin-btn admin-btn--primary"
              onClick={() => onSave(c)}
              disabled={!canSave}
              style={!canSave ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryEditor({ category, isNew, existingIds, onSave, onCancel }: {
  category: AdminCategory;
  isNew: boolean;
  existingIds: string[];
  onSave: (c: AdminCategory) => void;
  onCancel: () => void;
}) {
  const [c, setC] = useState<AdminCategory>(category);
  const idCollision = isNew && c.id.length > 0 && existingIds.includes(c.id);
  const idEmpty = isNew && c.id.trim().length === 0;
  const labelEmpty = c.label.trim().length === 0;
  const canSave = !idCollision && !idEmpty && !labelEmpty;

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3>{isNew ? "New Category" : `Rename: ${category.label}`}</h3>
        <div className="admin-form">
          <label>ID (unique, lowercase) {!isNew && <small>· locked on existing categories</small>}
            <input
              value={c.id}
              onChange={(e) => setC((prev) => ({ ...prev, id: e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase() }))}
              disabled={!isNew}
              style={idCollision ? { borderColor: "#e74c3c" } : undefined}
            />
            {idCollision && <small style={{ color: "#e74c3c", fontWeight: 500 }}>⚠ Already exists.</small>}
          </label>
          <label>Display Label
            <input
              value={c.label}
              onChange={(e) => {
                const newLabel = e.target.value;
                setC((prev) => {
                  const next = { ...prev, label: newLabel };
                  if (isNew) {
                    const prevSlug = slugifyId(prev.label);
                    if (!prev.id || prev.id === prevSlug) {
                      next.id = slugifyId(newLabel);
                    }
                  }
                  return next;
                });
              }}
            />
          </label>
          <div className="admin-form__actions">
            <button className="admin-btn" onClick={onCancel}>Cancel</button>
            <button
              className="admin-btn admin-btn--primary"
              onClick={() => onSave(c)}
              disabled={!canSave}
              style={!canSave ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Mockup Scenes Manager
// ═══════════════════════════════════════════════════
function ScenesManager() {
  const [scenes, setScenes] = useState<ScenesMap>({});
  const [activeType, setActiveType] = useState("gallery_canvas");
  const [editing, setEditing] = useState<{ productType: string; scene: SceneConfig } | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { scenes } = await adminScenes.list();
    setScenes(scenes);
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(m: string) {
    setMsg(m);
    if (!m.startsWith("❌")) setTimeout(() => setMsg(""), 2500);
  }

  const arr = scenes[activeType] ?? [];
  const types = Object.keys(scenes).length > 0 ? Object.keys(scenes) : ["photo_panel", "gallery_canvas", "framed_canvas"];

  const { draggedIdx, dragOverIdx, getDragHandlers } = useDragReorder(
    arr,
    (next) => setScenes({ ...scenes, [activeType]: next }),
    async (order) => {
      await adminScenes.reorder(activeType, order);
      flash("✅ Order saved");
    },
    (m) => {
      flash(`❌ Reorder failed: ${m}`);
      load();
    },
  );

  async function saveScene(productType: string, scene: SceneConfig): Promise<boolean> {
    try {
      const existing = (scenes[productType] ?? []).find((s) => s.id === scene.id);
      if (existing) {
        await adminScenes.update(productType, scene.id, scene);
      } else {
        await adminScenes.create(productType, scene);
      }
      // Refresh BEFORE closing modal so user sees the updated list under it,
      // and we know the API write actually landed (no race with editor close).
      await load();
      flash(existing ? "✅ Scene updated" : "✅ Scene created");
      setEditing(null);
      return true;
    } catch (e: unknown) {
      const err = e instanceof Error ? e.message : "Unknown error";
      console.error("[saveScene] failed:", e);
      flash(`❌ Save failed: ${err}${err.includes("unauthorized") || err.includes("401") ? " — please log out and log back in" : ""}`);
      return false;
    }
  }

  async function removeScene(productType: string, sceneId: string) {
    if (!confirm("Delete this scene?")) return;
    await adminScenes.delete(productType, sceneId);
    load();
    flash("Scene removed");
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>🖼 Mockup Scenes</h3>
        <button className="admin-btn admin-btn--primary" onClick={() => setEditing({
          productType: activeType,
          scene: { id: "", label: "", type: "room_scene", artPosition: { x: 0.3, y: 0.1, w: 0.4, h: 0.5 }, shadow: true, frameStyle: "none", enabled: true, sortOrder: arr.length }
        })}>
          + Add Scene
        </button>
      </div>

      <div className="admin-tabs">
        {types.map((t) => (
          <button key={t} className={`admin-tab ${t === activeType ? "active" : ""}`} onClick={() => setActiveType(t)}>
            {t.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {msg && (
        <div
          className={"admin-flash" + (msg.startsWith("❌") ? " admin-flash--error" : "")}
          onClick={() => setMsg("")}
          role="button"
          title="Click to dismiss"
        >
          {msg}
        </div>
      )}

      <div className="admin-grid admin-grid--mockups">
        {arr.map((scene, i) => (
          <div
            key={scene.id}
            {...getDragHandlers(i)}
            className={
              "admin-card" +
              (scene.enabled === false ? " admin-card--disabled" : "") +
              (draggedIdx === i ? " is-dragging" : "") +
              (dragOverIdx === i && draggedIdx !== i ? " is-drop-target" : "")
            }
          >
            <div className="admin-drag-handle" aria-hidden title="Drag to reorder">⋮⋮</div>
            <div className="admin-card__preview admin-card__preview--scene">
              {scene.bgImage ? (
                <img src={assetUrl(scene.bgImage)} alt={scene.label} />
              ) : (
                <div className="admin-card__flat-icon">📦</div>
              )}
              {scene.artPosition && (
                scene.artPosition.corners ? (
                  <svg className="admin-card__art-svg" viewBox="0 0 1 1" preserveAspectRatio="none">
                    <polygon
                      points={`${scene.artPosition.corners.tl[0]},${scene.artPosition.corners.tl[1]} ${scene.artPosition.corners.tr[0]},${scene.artPosition.corners.tr[1]} ${scene.artPosition.corners.br[0]},${scene.artPosition.corners.br[1]} ${scene.artPosition.corners.bl[0]},${scene.artPosition.corners.bl[1]}`}
                      fill="rgba(59,130,246,0.15)"
                      stroke="rgba(59,130,246,0.8)"
                      strokeWidth="0.006"
                      strokeDasharray="0.015 0.008"
                    />
                  </svg>
                ) : (
                  <div className="admin-card__art-overlay" style={{
                    left: `${scene.artPosition.x * 100}%`,
                    top: `${scene.artPosition.y * 100}%`,
                    width: `${scene.artPosition.w * 100}%`,
                    height: `${scene.artPosition.h * 100}%`,
                    transform: `rotate(${scene.artPosition.rotation ?? 0}deg) skewX(${scene.artPosition.skewX ?? 0}deg) skewY(${scene.artPosition.skewY ?? 0}deg)`,
                  }} />
                )
              )}
            </div>
            <div className="admin-card__body">
              <h4>{scene.label}</h4>
              <span className="admin-badge">{scene.type}</span>
              <code className="admin-card__id">{scene.id}</code>
            </div>
            <div className="admin-card__actions">
              <button className="admin-btn" onClick={() => setEditing({ productType: activeType, scene: { ...scene } })}>Edit</button>
              <button className="admin-btn" onClick={() => saveScene(activeType, { ...scene, enabled: scene.enabled !== false ? false : true })}>
                {scene.enabled !== false ? "Disable" : "Enable"}
              </button>
              <button className="admin-btn admin-btn--danger" onClick={() => removeScene(activeType, scene.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SceneEditor
          productType={editing.productType}
          scene={editing.scene}
          onSave={saveScene}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ── Live Composite Preview (right column of SceneEditor) ─────────────
// Polls /admin/preview-composite whenever the scene shape or test art
// changes. Debounced so dragging a corner doesn't spam the server.
// Stored across SceneEditor opens — admin uploads a test art once and re-uses
// it. Sits in sessionStorage so a page reload keeps the choice (memory only).
const CUSTOM_TEST_ART_KEY = "admin_test_art_url";

// Default test art — hardcoded to a known-good file shipped with the api.
// Falls back to first listed style if this file is missing. The point is to
// guarantee the panel renders SOMETHING on first open even before the admin
// uploads any custom art.
const DEFAULT_TEST_ART = "/styles/fire_cyberpunk.png";

function ScenePreviewPanel({ productType, scene, frameColor }: {
  productType: string;
  scene: SceneConfig;
  frameColor?: string;
}) {
  const [styles, setStyles] = useState<AdminStyle[]>([]);
  const [customArtUrl, setCustomArtUrl] = useState<string>(
    () => sessionStorage.getItem(CUSTOM_TEST_ART_KEY) ?? "",
  );
  const [testArtUrl, setTestArtUrl] = useState<string>(
    () => sessionStorage.getItem(CUSTOM_TEST_ART_KEY) || DEFAULT_TEST_ART,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const lastObjectUrl = useRef<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Zoom + pan state for inspecting fine corner alignment.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 6;

  function resetView() {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }

  /** Apply a zoom delta centered on (cx, cy) in frame-local pixels. */
  function zoomAt(nextZoom: number, cx: number, cy: number) {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, nextZoom));
    const factor = clamped / zoom;
    setPan({
      x: cx * (1 - factor) + pan.x * factor,
      y: cy * (1 - factor) + pan.y * factor,
    });
    setZoom(clamped);
  }

  function onWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (!previewUrl) return;
    e.preventDefault();
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // Exponential feel — small wheel ticks scale proportionally to current zoom.
    const next = zoom * (1 - e.deltaY * 0.0015);
    zoomAt(next, cx, cy);
  }

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.button !== 0 || !previewUrl) return;
    e.preventDefault();
    const startX = e.clientX - pan.x;
    const startY = e.clientY - pan.y;
    setDragging(true);
    const move = (ev: MouseEvent) => setPan({ x: ev.clientX - startX, y: ev.clientY - startY });
    const up = () => {
      setDragging(false);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  function zoomBtn(delta: number) {
    const rect = frameRef.current?.getBoundingClientRect();
    const cx = rect ? rect.width / 2 : 0;
    const cy = rect ? rect.height / 2 : 0;
    zoomAt(zoom * (1 + delta), cx, cy);
  }

  // Load styles once for the test-art picker. Only auto-default if no custom
  // upload is set yet.
  useEffect(() => {
    adminStyles.list()
      .then(({ styles }) => {
        const withImg = styles.filter((s) => s.imageUrl && s.enabled !== false);
        setStyles(withImg);
        if (!testArtUrl && withImg[0]?.imageUrl) setTestArtUrl(withImg[0].imageUrl);
      })
      .catch(() => {/* silent — preview panel just won't auto-default */});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const { url } = await uploadAsset(file, "styles");
      setCustomArtUrl(url);
      setTestArtUrl(url);
      sessionStorage.setItem(CUSTOM_TEST_ART_KEY, url);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function handlePasteUrl(value: string) {
    setCustomArtUrl(value);
    if (value) {
      setTestArtUrl(value);
      sessionStorage.setItem(CUSTOM_TEST_ART_KEY, value);
    } else {
      sessionStorage.removeItem(CUSTOM_TEST_ART_KEY);
    }
  }

  // Debounced refresh: 600ms after the last scene/art change.
  useEffect(() => {
    if (!testArtUrl) return;
    if (scene.type !== "room_scene" && scene.type !== "product_flat") return;
    if (scene.type === "room_scene" && !scene.bgImage) return;

    const id = setTimeout(async () => {
      setBusy(true);
      setErr(null);
      try {
        const blob = await previewComposite({
          product_type: productType,
          scene,
          art_url: testArtUrl,
          ...(frameColor ? { frame_color: frameColor } : {}),
        });
        const url = URL.createObjectURL(blob);
        if (lastObjectUrl.current) URL.revokeObjectURL(lastObjectUrl.current);
        lastObjectUrl.current = url;
        setPreviewUrl(url);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    }, 600);
    return () => clearTimeout(id);
  }, [productType, scene, testArtUrl, frameColor]);

  // Cleanup last object URL when component unmounts.
  useEffect(() => () => {
    if (lastObjectUrl.current) URL.revokeObjectURL(lastObjectUrl.current);
  }, []);

  return (
    <div className="admin-scene-preview">
      <div className="admin-scene-preview__head">
        <span className="admin-scene-preview__title">🖼 Live Composite</span>
        {busy && <span className="admin-scene-preview__busy">rendering…</span>}
      </div>

      <label className="admin-scene-preview__art-pick">
        Test art
        <select value={testArtUrl} onChange={(e) => setTestArtUrl(e.target.value)}>
          {customArtUrl && (
            <option value={customArtUrl}>📷 Custom upload</option>
          )}
          {/* Hardcoded fallback — always available, ships with api/uploads/styles */}
          {!styles.some((s) => s.imageUrl === DEFAULT_TEST_ART) && (
            <option value={DEFAULT_TEST_ART}>Default (Cyberpunk)</option>
          )}
          {styles.map((s) => (
            <option key={s.id} value={s.imageUrl}>{s.label}</option>
          ))}
        </select>
      </label>

      <div className="admin-scene-preview__upload-row">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleUpload}
        />
        <button
          type="button"
          className="admin-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : "📁 Upload art"}
        </button>
        <span className="admin-scene-preview__or">or</span>
        <input
          type="text"
          className="admin-scene-preview__url-input"
          placeholder="paste image URL"
          value={customArtUrl}
          onChange={(e) => handlePasteUrl(e.target.value)}
        />
      </div>

      <div
        ref={frameRef}
        className={"admin-scene-preview__frame" + (dragging ? " is-dragging" : zoom > 1 ? " is-zoomed" : "")}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onDoubleClick={resetView}
      >
        {previewUrl ? (
          <img
            src={assetUrl(previewUrl)}
            alt="Composite preview"
            draggable={false}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
            }}
          />
        ) : (
          <div className="admin-scene-preview__empty">
            {err ? `⚠ ${err}` : !testArtUrl
              ? "Upload or paste a test art URL to begin"
              : "Adjust corners — preview updates automatically"}
          </div>
        )}

        {previewUrl && (
          <div className="admin-scene-preview__controls" onMouseDown={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => zoomBtn(-0.2)} title="Zoom out" disabled={zoom <= ZOOM_MIN}>−</button>
            <span className="admin-scene-preview__zoom-label">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => zoomBtn(0.25)} title="Zoom in" disabled={zoom >= ZOOM_MAX}>+</button>
            <button type="button" onClick={resetView} title="Reset (or double-click)" disabled={zoom === 1 && pan.x === 0 && pan.y === 0}>⤾</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SceneEditor({ productType, scene, onSave, onCancel }: {
  productType: string;
  scene: SceneConfig;
  onSave: (productType: string, scene: SceneConfig) => Promise<boolean>;
  onCancel: () => void;
}) {
  // Save in flight — disable button + show "Saving…" so user can't double-submit
  // (which previously created duplicate scenes like ga_64f419a3 + ga_c557f8a7).
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveErr(null);
    try {
      const ok = await onSave(productType, s);
      // Modal closes on success via parent setEditing(null). On failure parent
      // shows flash banner AND we surface inline error here too (modal may
      // visually hide the flash banner).
      if (!ok) setSaveErr("Save failed — see banner behind this dialog, or check console.");
    } finally {
      setSaving(false);
    }
  }
  const [s, setS] = useState<SceneConfig>(() => {
    // Auto-init corners from x,y,w,h if not present
    const p = scene.artPosition ?? { x: 0.3, y: 0.1, w: 0.4, h: 0.5 };
    if (!p.corners) {
      return { ...scene, artPosition: { ...p, corners: {
        tl: [p.x, p.y], tr: [p.x + p.w, p.y],
        bl: [p.x, p.y + p.h], br: [p.x + p.w, p.y + p.h],
      }}};
    }
    return scene;
  });
  const [uploading, setUploading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoDetectMsg, setAutoDetectMsg] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragCorner = useRef<string | null>(null);
  const dragOffset = useRef({ dx: 0, dy: 0 });

  function set<K extends keyof SceneConfig>(k: K, v: SceneConfig[K]) {
    setS((prev) => ({ ...prev, [k]: v }));
  }

  const defaultCorners = { tl: [0.3, 0.1] as [number, number], tr: [0.7, 0.1] as [number, number], bl: [0.3, 0.6] as [number, number], br: [0.7, 0.6] as [number, number] };
  const pos = s.artPosition ?? { x: 0.3, y: 0.1, w: 0.4, h: 0.5, corners: defaultCorners };
  const c = pos.corners ?? defaultCorners;

  function setCorners(nc: typeof c) {
    const xs = [nc.tl[0], nc.tr[0], nc.bl[0], nc.br[0]];
    const ys = [nc.tl[1], nc.tr[1], nc.bl[1], nc.br[1]];
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    set("artPosition", { ...pos, corners: nc,
      x: Math.round(minX * 100) / 100, y: Math.round(minY * 100) / 100,
      w: Math.round((maxX - minX) * 100) / 100, h: Math.round((maxY - minY) * 100) / 100,
    });
  }

  async function handleBgUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const { url } = await uploadAsset(file, "mockups"); set("bgImage", url); } catch {}
    setUploading(false);
  }

  function toNorm(e: MouseEvent | React.MouseEvent) {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { nx: 0, ny: 0 };
    return { nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height };
  }

  function startCornerDrag(corner: string, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    dragCorner.current = corner;
    const { nx, ny } = toNorm(e);
    const pt = c[corner as keyof typeof c];
    dragOffset.current = { dx: pt[0] - nx, dy: pt[1] - ny };
    const onMove = (ev: MouseEvent) => {
      if (!dragCorner.current) return;
      const { nx: mx, ny: my } = toNorm(ev);
      const key = dragCorner.current as keyof typeof c;
      const newX = Math.max(0, Math.min(1, mx + dragOffset.current.dx));
      const newY = Math.max(0, Math.min(1, my + dragOffset.current.dy));
      setCorners({ ...c, [key]: [Math.round(newX * 1000) / 1000, Math.round(newY * 1000) / 1000] });
    };
    const onUp = () => { dragCorner.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function startPolyDrag(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const { nx, ny } = toNorm(e);
    const orig = { ...c, mx: nx, my: ny };
    const onMove = (ev: MouseEvent) => {
      const { nx: mx, ny: my } = toNorm(ev);
      const dx = mx - orig.mx, dy = my - orig.my;
      setCorners({
        tl: [Math.max(0, Math.min(1, orig.tl[0] + dx)), Math.max(0, Math.min(1, orig.tl[1] + dy))],
        tr: [Math.max(0, Math.min(1, orig.tr[0] + dx)), Math.max(0, Math.min(1, orig.tr[1] + dy))],
        bl: [Math.max(0, Math.min(1, orig.bl[0] + dx)), Math.max(0, Math.min(1, orig.bl[1] + dy))],
        br: [Math.max(0, Math.min(1, orig.br[0] + dx)), Math.max(0, Math.min(1, orig.br[1] + dy))],
      });
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function resetToRect() {
    const p = { x: 0.2, y: 0.15, w: 0.4, h: 0.5 };
    set("artPosition", { ...pos, ...p, corners: {
      tl: [p.x, p.y], tr: [p.x + p.w, p.y], bl: [p.x, p.y + p.h], br: [p.x + p.w, p.y + p.h],
    }});
  }

  async function autoDetect() {
    if (!s.bgImage) return;
    setAutoDetecting(true);
    setAutoDetectMsg(null);
    try {
      const { analysis } = await analyzeMockupBg(s.bgImage);
      setCorners(analysis.corners);
      // Pre-fill empty fields, never overwrite user-typed values.
      setS((prev) => ({
        ...prev,
        label: prev.label || analysis.label_suggestion,
        type: prev.type || analysis.scene_type_suggestion,
        frameStyle: prev.frameStyle || analysis.frame_style_suggestion,
      }));
      setAutoDetectMsg(`✓ ${analysis.notes}`);
    } catch (e) {
      setAutoDetectMsg(`⚠ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setAutoDetecting(false);
    }
  }

  const cornerLabels: { key: keyof typeof c; label: string }[] = [
    { key: "tl", label: "TL" }, { key: "tr", label: "TR" },
    { key: "bl", label: "BL" }, { key: "br", label: "BR" },
  ];

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal admin-modal--xwide" onClick={(e) => e.stopPropagation()}>
        <h3>{scene.id ? `Edit Scene: ${scene.label}` : "New Scene"}</h3>
        <div className="admin-form">
          <label>Label <input value={s.label} onChange={(e) => set("label", e.target.value)} /></label>
          <label>Type
            <select value={s.type} onChange={(e) => set("type", e.target.value as SceneConfig["type"])}>
              <option value="product_flat">Product Flat</option>
              <option value="room_scene">Room Scene</option>
            </select>
          </label>
          {s.type === "room_scene" && (<>
            <label>Background Image
              <input type="file" accept="image/*" onChange={handleBgUpload} />
              {uploading && <span>Uploading...</span>}
            </label>
            {s.bgImage && (
              <div className="admin-scene-edit-layout">
              <div className="admin-art-editor">
                <p className="admin-art-editor__hint">
                  🎯 Drag corners for perspective · Drag polygon to move · Each corner is independent
                </p>
                <div ref={canvasRef} className="admin-art-editor__canvas">
                  <img src={assetUrl(s.bgImage)} alt="Background" draggable={false} />
                  <svg className="admin-art-editor__svg" viewBox="0 0 1 1" preserveAspectRatio="none">
                    {/* Fill polygon */}
                    <polygon
                      points={`${c.tl[0]},${c.tl[1]} ${c.tr[0]},${c.tr[1]} ${c.br[0]},${c.br[1]} ${c.bl[0]},${c.bl[1]}`}
                      fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth="0.004"
                      style={{ cursor: "move" }}
                      onMouseDown={startPolyDrag}
                    />
                    {/* Edge lines */}
                    {([[c.tl,c.tr],[c.tr,c.br],[c.br,c.bl],[c.bl,c.tl]] as [number[], number[]][]).map(([a,b], i) => (
                      <line key={i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                        stroke="#3b82f6" strokeWidth="0.005" strokeDasharray="0.012 0.006" />
                    ))}
                    {/* Diagonal guides */}
                    <line x1={c.tl[0]} y1={c.tl[1]} x2={c.br[0]} y2={c.br[1]}
                      stroke="rgba(59,130,246,0.2)" strokeWidth="0.002" strokeDasharray="0.008 0.008" />
                    <line x1={c.tr[0]} y1={c.tr[1]} x2={c.bl[0]} y2={c.bl[1]}
                      stroke="rgba(59,130,246,0.2)" strokeWidth="0.002" strokeDasharray="0.008 0.008" />
                  </svg>
                  {/* Corner handles */}
                  {cornerLabels.map(({ key, label }) => (
                    <div key={key} className="admin-art-editor__corner"
                      style={{ left: `${c[key][0] * 100}%`, top: `${c[key][1] * 100}%` }}
                      onMouseDown={(e) => startCornerDrag(key, e)} title={`Drag ${label}`}>
                      <span className="admin-art-editor__corner-label">{label}</span>
                    </div>
                  ))}
                </div>
                {/* Corner coords */}
                <div className="admin-art-editor__corner-values">
                  {cornerLabels.map(({ key, label }) => (
                    <div key={key} className="admin-art-editor__corner-group">
                      <span className="admin-art-editor__corner-tag">{label}</span>
                      <input type="number" step={0.01} value={c[key][0].toFixed(3)}
                        onChange={(e) => setCorners({ ...c, [key]: [+e.target.value, c[key][1]] })} />
                      <input type="number" step={0.01} value={c[key][1].toFixed(3)}
                        onChange={(e) => setCorners({ ...c, [key]: [c[key][0], +e.target.value] })} />
                    </div>
                  ))}
                </div>
                <div className="admin-art-editor__actions">
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={autoDetect}
                    disabled={autoDetecting}
                    title="Use Gemini Vision to find the art region automatically"
                  >
                    {autoDetecting ? "✨ Detecting…" : "✨ Auto-detect corners"}
                  </button>
                  <button type="button" className="admin-btn" onClick={resetToRect}>
                    ↺ Reset
                  </button>
                </div>
                {autoDetectMsg && (
                  <div className={"admin-art-editor__msg" + (autoDetectMsg.startsWith("⚠") ? " is-error" : "")}>
                    {autoDetectMsg}
                  </div>
                )}
              </div>
              <ScenePreviewPanel productType={productType} scene={s} />
              </div>
            )}
            <label>Frame Style
              <select value={s.frameStyle ?? "none"} onChange={(e) => set("frameStyle", e.target.value)}>
                <option value="none">None</option>
                <option value="thin_white">Thin White</option>
                <option value="thin_black">Thin Black</option>
                <option value="colored">Colored (uses frame color)</option>
              </select>
            </label>
          </>)}
          {saveErr && (
            <div
              className="admin-flash admin-flash--error"
              style={{ position: "static", margin: "12px 0 0" }}
              role="alert"
            >
              ❌ {saveErr}
            </div>
          )}
          <div className="admin-form__actions">
            <button className="admin-btn" onClick={onCancel} disabled={saving}>Cancel</button>
            <button
              className="admin-btn admin-btn--primary"
              onClick={handleSave}
              disabled={saving}
              style={saving ? { opacity: 0.6, cursor: "wait" } : undefined}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Products Manager
// ═══════════════════════════════════════════════════
function ProductsManager() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [editing, setEditing] = useState<AdminProduct | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { products } = await adminProducts.list();
    setProducts(products);
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(m: string) {
    setMsg(m);
    if (!m.startsWith("❌")) setTimeout(() => setMsg(""), 2500);
  }

  async function saveProduct(p: AdminProduct) {
    try {
      await adminProducts.update(p.type, p);
      flash("✅ Product updated");
      setEditing(null);
      load();
    } catch (e: unknown) {
      flash(`❌ ${e instanceof Error ? e.message : "Error"}`);
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>📦 Products</h3>
      </div>
      {msg && (
        <div
          className={"admin-flash" + (msg.startsWith("❌") ? " admin-flash--error" : "")}
          onClick={() => setMsg("")}
          role="button"
          title="Click to dismiss"
        >
          {msg}
        </div>
      )}

      <div className="admin-grid">
        {products.map((p) => (
          <div key={p.type} className="admin-card">
            <div className="admin-card__body">
              <h4>{p.label}</h4>
              <span className="admin-badge">Printify #{p.printifyId}</span>
              <p className="admin-card__tagline">{p.subtitle}</p>
              <div className="admin-card__sizes">
                {p.sizes.map((sz) => (
                  <span key={sz.label} className="admin-size-chip">
                    {sz.label} — ${sz.retailPrice}
                  </span>
                ))}
              </div>
              {p.frameColors && (
                <div className="admin-card__colors">
                  {p.frameColors.map((c) => <span key={c} className="admin-color-chip">{c}</span>)}
                </div>
              )}
            </div>
            <div className="admin-card__actions">
              <button className="admin-btn" onClick={() => setEditing({ ...p })}>Edit</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProductEditor product={editing} onSave={saveProduct} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function ProductEditor({ product, onSave, onCancel }: {
  product: AdminProduct;
  onSave: (p: AdminProduct) => void;
  onCancel: () => void;
}) {
  const [p, setP] = useState<AdminProduct>(product);

  function updateSize(idx: number, field: string, val: string | number) {
    const sizes = [...p.sizes];
    const current = sizes[idx];
    if (!current) return;
    sizes[idx] = { ...current, [field]: typeof val === "string" && field !== "label" ? parseFloat(val) || 0 : val };
    setP({ ...p, sizes });
  }

  function addSize() {
    setP({ ...p, sizes: [...p.sizes, { label: '12×16"', width: 12, height: 16, printifyCost: 0, retailPrice: 0 }] });
  }

  function removeSize(idx: number) {
    setP({ ...p, sizes: p.sizes.filter((_, i) => i !== idx) });
  }

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal admin-modal--wide" onClick={(e) => e.stopPropagation()}>
        <h3>Edit: {product.label}</h3>
        <div className="admin-form">
          <label>Label <input value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} /></label>
          <label>Subtitle <input value={p.subtitle} onChange={(e) => setP({ ...p, subtitle: e.target.value })} /></label>
          <label>Material <textarea rows={2} value={p.material} onChange={(e) => setP({ ...p, material: e.target.value })} /></label>

          <h4>Sizes & Pricing</h4>
          <table className="admin-table">
            <thead>
              <tr><th>Label</th><th>W"</th><th>H"</th><th>Cost</th><th>Price</th><th></th></tr>
            </thead>
            <tbody>
              {p.sizes.map((sz, i) => (
                <tr key={i}>
                  <td><input value={sz.label} onChange={(e) => updateSize(i, "label", e.target.value)} /></td>
                  <td><input type="number" value={sz.width} onChange={(e) => updateSize(i, "width", e.target.value)} /></td>
                  <td><input type="number" value={sz.height} onChange={(e) => updateSize(i, "height", e.target.value)} /></td>
                  <td><input type="number" step={0.01} value={sz.printifyCost} onChange={(e) => updateSize(i, "printifyCost", e.target.value)} /></td>
                  <td><input type="number" value={sz.retailPrice} onChange={(e) => updateSize(i, "retailPrice", e.target.value)} /></td>
                  <td><button className="admin-btn admin-btn--danger" onClick={() => removeSize(i)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="admin-btn" onClick={addSize}>+ Add Size</button>

          <div className="admin-form__actions">
            <button className="admin-btn" onClick={onCancel}>Cancel</button>
            <button className="admin-btn admin-btn--primary" onClick={() => onSave(p)}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Orders Manager — paid orders + print-file downloads
// ═══════════════════════════════════════════════════
function OrdersManager() {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminOrders
      .list()
      .then((r) => { setOrders(r.orders); setErr(null); })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="admin-orders__msg">Loading orders…</p>;
  if (err) return <p className="admin-orders__msg admin-orders__msg--err">⚠ {err}</p>;

  return (
    <div>
      <div className="admin-orders__head">
        <h2>Paid Orders — print files</h2>
        <button className="admin-btn" onClick={load}>↻ Refresh</button>
      </div>
      {orders.length === 0 && (
        <p className="admin-orders__msg">No paid orders yet.</p>
      )}
      {orders.map((o) => (
        <div key={o.order_number} className="admin-order">
          <div className="admin-order__top">
            <strong>#{o.order_number}</strong>
            <span>{o.email ?? "—"}</span>
            <span>{new Date(o.created_at).toLocaleString()}</span>
            {o.total_price && <span>{o.total_price} {o.currency ?? ""}</span>}
          </div>
          {o.items.map((it, i) => (
            <div key={i} className="admin-order__item">
              {it.preview_url ? (
                <img className="admin-order__thumb" src={it.preview_url} alt="" />
              ) : (
                <div className="admin-order__thumb admin-order__thumb--empty" />
              )}
              <div className="admin-order__info">
                <div className="admin-order__title">
                  {it.dad_name ? `${it.dad_name} — ` : ""}
                  {it.product_title}
                </div>
                <div className="admin-order__meta">
                  Qty {it.quantity}
                  {it.style_id ? ` · ${it.style_id}` : ""}
                </div>
              </div>
              {it.print_url ? (
                <a
                  className="admin-btn admin-btn--primary"
                  href={it.print_url}
                  target="_blank"
                  rel="noopener"
                  download
                >
                  ⬇ Print file
                </a>
              ) : (
                <span
                  className={
                    "admin-order__status" +
                    (it.print_status === "failed" ? " admin-order__status--err" : "")
                  }
                >
                  {it.print_status === "upscaling"
                    ? "⏳ Building…"
                    : it.print_status === "failed"
                      ? "⚠ Failed"
                      : "Not ready"}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Main Admin Panel
// ═══════════════════════════════════════════════════
export function AdminPanel() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("admin_token"));
  const [tab, setTab] = useState<"styles" | "categories" | "collections" | "scenes" | "products" | "orders">("styles");

  if (!authed) return <LoginGate onLogin={() => setAuthed(true)} />;

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>⚙️ MONOGATARI Admin</h1>
        <button className="admin-btn" onClick={() => { sessionStorage.removeItem("admin_token"); setAuthed(false); }}>
          Logout
        </button>
      </header>

      <nav className="admin-nav">
        <button className={`admin-nav__btn ${tab === "styles" ? "active" : ""}`} onClick={() => setTab("styles")}>
          📋 Templates
        </button>
        <button className={`admin-nav__btn ${tab === "categories" ? "active" : ""}`} onClick={() => setTab("categories")}>
          🏷 Categories
        </button>
        <button className={`admin-nav__btn ${tab === "collections" ? "active" : ""}`} onClick={() => setTab("collections")}>
          📚 Collections
        </button>
        <button className={`admin-nav__btn ${tab === "scenes" ? "active" : ""}`} onClick={() => setTab("scenes")}>
          🖼 Mockups
        </button>
        <button className={`admin-nav__btn ${tab === "products" ? "active" : ""}`} onClick={() => setTab("products")}>
          📦 Products
        </button>
        <button className={`admin-nav__btn ${tab === "orders" ? "active" : ""}`} onClick={() => setTab("orders")}>
          🧾 Orders
        </button>
      </nav>

      <main className="admin-main">
        {tab === "styles" && <StylesManager />}
        {tab === "categories" && <CategoriesManager />}
        {tab === "collections" && <CollectionsManager />}
        {tab === "scenes" && <ScenesManager />}
        {tab === "products" && <ProductsManager />}
        {tab === "orders" && <OrdersManager />}
      </main>
    </div>
  );
}
